import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip,
  Legend,
  type ChartOptions,
  type ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { observer } from 'mobx-react-lite';
import { useCapacityStore } from '../store/CapacityPlannerContext';
import styles from './cpTrendChart.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Title, Tooltip, Legend);

// ---- helpers ----------------------------------------------------------------

function formatMonth(iso: string): string {
  const [year, month] = iso.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
}

// ---- chart config -----------------------------------------------------------

const CHART_OPTIONS: ChartOptions<'line'> = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top' as const,
      labels: {
        font: { size: 11 },
        usePointStyle: true,
        pointStyleWidth: 10,
      },
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { font: { size: 11 } },
    },
    y: {
      beginAtZero: false,
      grid: { color: '#f1f5f9' },
      ticks: { font: { size: 11 }, precision: 0 },
      title: {
        display: true,
        text: 'Headcount (FTE)',
        font: { size: 11 },
        color: '#64748b',
      },
    },
  },
};

// ---- component --------------------------------------------------------------

const CPTrendChart = observer(() => {
  const store = useCapacityStore();
  const { dashboardData } = store;

  if (!dashboardData) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title}>
          <i className="pi pi-chart-line" />
          Required Headcount by Month
        </div>
        <div className={styles.chartContainer} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>No data available</span>
        </div>
      </div>
    );
  }

  const months = store.filteredMonths;
  const { grand_total } = dashboardData;
  const labels = months.map(formatMonth);

  const chartData: ChartData<'line'> = {
    labels,
    datasets: [
      {
        label: 'PA',
        data: months.map((m) => grand_total[m]?.pa_hc ?? 0),
        borderColor: '#522d6e',
        backgroundColor: 'rgba(82,45,110,0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'MO',
        data: months.map((m) => grand_total[m]?.mo_hc ?? 0),
        borderColor: '#34b53a',
        backgroundColor: 'rgba(52,181,58,0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'QI',
        data: months.map((m) => grand_total[m]?.qi_hc ?? 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245,158,11,0.08)',
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: 'Total',
        data: months.map((m) => grand_total[m]?.total_hc ?? 0),
        borderColor: '#0f172a',
        borderDash: [6, 3] as number[],
        fill: false,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
    ],
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.title}>
        <i className="pi pi-chart-line" />
        Required Headcount by Month
      </div>
      <div className={styles.chartContainer}>
        <Line data={chartData} options={CHART_OPTIONS} />
      </div>
    </div>
  );
});

export default CPTrendChart;
